<template>
  <div class="min-h-[calc(100vh-120px)] flex flex-col">
    <div v-if="!isBriefingMode" class="flex flex-col flex-1">
      <!-- Hero Section - Centered Input -->
      <div class="flex-1 flex flex-col items-center justify-center py-8 md:py-12">
        <div class="text-center mb-8 animate-[fadeIn_0.8s_ease-out]">
          <h2 class="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
            今天有什么可以帮你？<br/>
            <span class="text-lg md:text-xl font-medium text-slate-600">输入你的管理痛点，AI将为你自动匹配自身管理者的实战经验</span>
          </h2>
          <div v-if="context.pressure > 8" class="flex justify-center mb-8 animate-[slideUp_0.5s_ease-out]">
            <div class="bg-red-500/10 border border-red-500/50 px-6 py-2.5 rounded-full flex items-center gap-3 shadow-[0_0_20px_rgba(239,68,68,0.2)]">
              <div class="w-2 h-2 rounded-full bg-red-500 animate-ping"></div>
              <span class="text-xs font-black text-red-500 uppercase tracking-widest">
                紧急军事通报：监测到高压爆仓风险，建议立即阅研《扩张期人才防御建议》
              </span>
              <IconArrowRight class="w-4 h-4 text-red-500" />
            </div>
          </div>
        </div>

        <div class="w-full max-w-3xl mx-auto px-4 animate-[slideUp_0.6s_ease-out]">
          <IntentionCapture
            mode="new-search"
            @search="handleSearch"
            @start-diagnose="$router.push('/diagnose-start')"
          />
        </div>
      </div>

      <!-- Content Grid - Below Input -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-12 animate-[fadeIn_1s_ease-out]">
        <!-- 1. 个人高频痛点 TOP 10 -->
        <div class="bg-white/60 backdrop-blur-xl rounded-[32px] border border-white/80 flex flex-col overflow-hidden group hover:border-[#F2C94C]/40 hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)] transition-all duration-300">
          <div class="p-6 border-b border-black/5 flex items-center gap-3">
            <IconTrophy class="w-5 h-5 text-[#F2C94C]" />
            <h3 class="text-sm font-black text-[#0A0F1D]/80 uppercase tracking-[0.2em]">个人高频痛点 TOP 10</h3>
          </div>
          <div class="flex-1 overflow-y-auto p-4 space-y-2">
            <button
              v-for="(topic, idx) in top10Topics"
              :key="topic.id"
              @click="handleTopicClick(topic)"
              class="w-full text-left p-4 hover:bg-white/80 rounded-2xl flex items-center gap-6 group transition-all duration-300"
            >
              <span class="text-sm font-black text-[#F2C94C]/60 group-hover:text-[#F2C94C] group-hover:scale-110 transition-all duration-300">
                {{ (idx + 1).toString().padStart(2, '0') }}
              </span>
              <span class="flex-1 text-sm font-bold text-gray-600 group-hover:text-[#0A0F1D]">{{ topic.title }}</span>
            </button>
          </div>
        </div>

        <!-- 2. 公司热点话题 TOP 10 (中间位置) -->
        <div class="bg-white/60 backdrop-blur-xl rounded-[32px] border border-white/80 flex flex-col overflow-hidden group hover:border-[#F2C94C]/40 hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)] transition-all duration-300">
          <div class="p-6 border-b border-black/5 flex items-center gap-3">
            <IconFlame class="w-5 h-5 text-orange-500 fill-orange-500" />
            <h3 class="text-sm font-black text-[#0A0F1D]/80 uppercase tracking-[0.2em]">公司热点话题 TOP 10</h3>
          </div>
          <div class="flex-1 overflow-y-auto p-4 space-y-2">
            <button
              v-for="(topic, idx) in hotTopics"
              :key="topic.id"
              @click="handleTopicClick(topic)"
              class="w-full text-left p-4 hover:bg-white/80 rounded-2xl flex items-center gap-6 group transition-all duration-300"
            >
              <span class="text-sm font-black text-orange-500/60 group-hover:text-orange-500 group-hover:scale-110 transition-all duration-300">
                {{ (idx + 1).toString().padStart(2, '0') }}
              </span>
              <span class="flex-1 text-sm font-bold text-gray-600 group-hover:text-[#0A0F1D]">{{ topic.title }}</span>
            </button>
          </div>
        </div>

        <ExpertLeaderboard :experts="experts" @expert-click="handleExpertClick" />
      </div>
    </div>

    <div v-else class="space-y-6">
      <button @click="setIsBriefingMode(false)" class="flex items-center text-sm font-bold text-[#0A0F1D] hover:text-[#F2C94C] transition-colors">
        <IconArrowRight class="w-4 h-4 mr-2 rotate-180" /> 返回搜索
      </button>
      <div v-if="selectedTopic">
        <DiagnosticResultView
          v-if="selectedTopic.id === 'custom' || selectedTopic.id === 'diagnostic-result'"
          :topic="selectedTopic"
          :prescription="customPrescription"
          :experts="EXPERTS"
          :context="context"
          :diagnostic-context="diagnosticContext"
          :is-generating="isGeneratingFeedback"
          :chat-history="activeChatHistory"
          @update-history="handleUpdateHistory"
        />
        <StudyDetailView
          v-else
          :topic="selectedTopic"
          :experts="EXPERTS"
          :context="context"
          @navigate-to-practice="handleNavigateToPractice"
          @navigate-to-diagnosis="handleNavigateToDiagnosis"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { TOPICS, EXPERTS, PRESCRIPTION_DATA } from '../data'
import IntentionCapture from '../components/practice/IntentionCapture.vue'
import ExpertLeaderboard from '../components/expert/ExpertLeaderboard.vue'
import DiagnosticResultView from '../components/diagnose/DiagnosticResultView.vue'
import StudyDetailView from '../components/study/StudyDetailView.vue'
import { IconTrophy, IconFlame, IconArrowRight } from '../components/icons'

export default {
  name: 'HomePage',
  components: {
    IntentionCapture,
    ExpertLeaderboard,
    DiagnosticResultView,
    StudyDetailView,
    IconTrophy,
    IconFlame,
    IconArrowRight
  },
  data() {
    return {
      TOPICS,
      EXPERTS,
      PRESCRIPTION_DATA
    }
  },
  computed: {
    ...mapState([
      'isBriefingMode', 'context', 'selectedTopic', 'diagnosticContext',
      'isGeneratingFeedback', 'activeHistoryId', 'history', 'experts'
    ]),
    top10Topics() {
      return TOPICS.filter(t => t.isTop10).slice(0, 10)
    },
    hotTopics() {
      return TOPICS.filter(t => t.isHot).slice(0, 10)
    },
    customPrescription() {
      const aiFeedback = this.$store.state.aiFeedback
      return {
        truth: aiFeedback.split('\n\n')[0] || '正在剖析真相...',
        summary: aiFeedback.split('\n\n')[1] || '生成建议中...',
        script: { opening: '“我们来聊聊这件事...”', responses: ['正在生成话术...'], closing: '“按此执行即可。”' },
        redLines: ['正在划定红线...']
      }
    },
    activeChatHistory() {
      return this.history.find(h => h.id === this.activeHistoryId)?.chatHistory || []
    }
  },
  methods: {
    ...mapMutations([
      'SET_IS_BRIEFING_MODE', 'SET_SELECTED_TOPIC', 'SET_PENDING_QUERY',
      'SET_TARGET_TOPIC_ID', 'SET_ACTIVE_PRESCRIPTION'
    ]),
    handleSearch(query, customDiagnosticContext) {
      this.$parent.handleSearch(query, customDiagnosticContext)
    },
    handleTopicClick(topic) {
      this.SET_ACTIVE_PRESCRIPTION(null)
      this.SET_SELECTED_TOPIC(topic)
      this.SET_PENDING_QUERY(topic.title)
      this.$router.push(`/topic/${topic.id}`)
    },
    handleExpertClick(expert) {
      this.$parent.handleExpertClick(expert)
    },
    handleUpdateHistory(chatHistory) {
      this.$parent.handleUpdateHistory(chatHistory)
    },
    handleNavigateToPractice(id) {
      this.SET_TARGET_TOPIC_ID(id)
      this.$router.push('/practice')
    },
    handleNavigateToDiagnosis(id) {
      this.SET_TARGET_TOPIC_ID(id)
      const t = TOPICS.find(topic => topic.id === id)
      if (t) this.SET_PENDING_QUERY(t.title)
      this.$router.push('/diagnose-start')
    }
  }
}
</script>
